// The phone's photo upload queue, for every page. `UploadQueueProvider` is mounted once in
// the root layout; capture screens and the computer upload dialog call `useUploadQueue()`.
//
// One tab per browser is the worker (elected with a Web Lock, so two tabs never send the
// same photo); every tab reads the same IndexedDB queue and hears changes from the others.

import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { logger } from "@/lib/logger";
import { captureKeys, checklistKeys, projectKeys } from "@/lib/query";
import {
  applyResult,
  backToWaiting,
  cleanupOnStart,
  createUpload,
  orphanedSending,
  pickNext,
  type QueuedUpload,
  type QueueSummary,
  sizeError,
  summarize,
  type UploadInput,
} from "@/lib/upload-queue";
import { deleteItem, getItem, listItems, putItem, subscribe } from "@/lib/upload-queue-store";
import { sendUpload } from "@/lib/upload-sender";

const CONCURRENCY = 3;
const WORKER_LOCK = "mesh-capture-worker";
const WIFI_ONLY_KEY = "mesh-capture-wifi-only";
/** Several photos landing together refresh the project's screens once. */
const INVALIDATE_AFTER_MS = 1_000;
/** Re-check which photos have gone stale this often while any are still to go up. */
const STALE_CHECK_MS = 60_000;
/** The abort reason when the provider unmounts, as opposed to a discard. */
const UNMOUNTED = "unmounted";

const hasLocks = () =>
  typeof navigator !== "undefined" &&
  "locks" in navigator &&
  typeof navigator.locks?.request === "function";

interface UploadQueue {
  items: QueuedUpload[];
  summary: QueueSummary;
  enqueue(input: UploadInput): Promise<string>;
  retryAll(): void;
  retry(id: string): void;
  keepDuplicate(id: string): void;
  discard(id: string): Promise<void>;
  clearSent(): Promise<void>;
  wifiOnly: boolean;
  setWifiOnly(v: boolean): void;
  wifiOnlySupported: boolean;
}

// ── Network Information API (Chrome on Android; missing on iOS and desktop Safari) ──

interface NetworkConnection extends EventTarget {
  type?: string;
}

function isConnection(value: unknown): value is NetworkConnection {
  return typeof value === "object" && value !== null && "addEventListener" in value;
}

function networkConnection(): NetworkConnection | null {
  if (typeof navigator === "undefined" || !("connection" in navigator)) return null;
  const connection = navigator.connection;
  return isConnection(connection) ? connection : null;
}

/** Wi-Fi only needs a connection that says what it is; desktop Chrome has one that never does. */
function detectWifiOnlySupport(): boolean {
  return typeof networkConnection()?.type === "string";
}

function readWifiOnly(): boolean {
  try {
    return localStorage.getItem(WIFI_ONLY_KEY) === "1";
  } catch {
    return false;
  }
}

function writeWifiOnly(value: boolean) {
  try {
    if (value) localStorage.setItem(WIFI_ONLY_KEY, "1");
    else localStorage.removeItem(WIFI_ONLY_KEY);
  } catch {
    // Private mode or blocked storage: the switch holds for this visit only.
  }
}

let persistAsked = false;

/** Ask once that the browser not evict the queue under storage pressure. */
function askPersistentStorage() {
  if (persistAsked) return;
  persistAsked = true;
  if (typeof navigator === "undefined" || !("storage" in navigator)) return;
  const persist = navigator.storage?.persist?.bind(navigator.storage);
  if (!persist) return;
  persist().catch((error: unknown) => {
    logger.warn("upload_queue_persist_failed", { error: String(error) });
  });
}

// ── The queue as React sees it: IndexedDB rows plus this tab's live send progress ──

let rows: QueuedUpload[] = [];
const liveProgress = new Map<string, number>();
let snapshot: QueuedUpload[] = [];
const viewers = new Set<() => void>();
let refreshSeq = 0;

function publish() {
  snapshot = rows.map((row) => {
    const progress = liveProgress.get(row.id);
    return progress === undefined ? row : { ...row, progress };
  });
  for (const viewer of viewers) viewer();
}

async function refreshRows() {
  const seq = ++refreshSeq;
  try {
    const list = await listItems();
    // A later refresh started while this one read; it has the newer rows.
    if (seq !== refreshSeq) return;
    rows = list.sort((a, b) => a.createdAt - b.createdAt);
    publish();
  } catch (error) {
    logger.warn("upload_queue_read_failed", { error: String(error) });
  }
}

function setProgress(id: string, fraction: number) {
  const rounded = Math.round(Math.min(1, Math.max(0, fraction)) * 100) / 100;
  if (liveProgress.get(id) === rounded) return;
  liveProgress.set(id, rounded);
  publish();
}

function clearProgress(id: string) {
  if (liveProgress.delete(id)) publish();
}

function watchSnapshot(viewer: () => void) {
  viewers.add(viewer);
  return () => {
    viewers.delete(viewer);
  };
}

const getSnapshot = () => snapshot;

// ── Provider ──

const UploadQueueContext = createContext<UploadQueue | null>(null);

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const items = useSyncExternalStore(watchSnapshot, getSnapshot, getSnapshot);
  const [wifiOnly, setWifiOnlyState] = useState(readWifiOnly);
  const [wifiOnlySupported] = useState(detectWifiOnlySupport);

  const wifiOnlyRef = useRef(wifiOnly);
  const inFlight = useRef(new Map<string, AbortController>());
  const tickRef = useRef<() => void>(() => {});
  const invalidateTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const invalidateProject = useCallback(
    (projectId: string) => {
      if (invalidateTimers.current.has(projectId)) return;
      const timer = setTimeout(() => {
        invalidateTimers.current.delete(projectId);
        void queryClient.invalidateQueries({ queryKey: captureKeys.all });
        void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        void queryClient.invalidateQueries({ queryKey: checklistKeys.project(projectId) });
      }, INVALIDATE_AFTER_MS);
      invalidateTimers.current.set(projectId, timer);
    },
    [queryClient],
  );

  // Every tab shows the queue and hears every other tab's changes.
  useEffect(() => {
    void refreshRows();
    return subscribe(() => {
      void refreshRows();
      tickRef.current();
    });
  }, []);

  // The worker: only in the tab holding the lock (or every tab, where locks are missing).
  useEffect(() => {
    let leader = false;
    let stopped = false;
    let wakeTimer: ReturnType<typeof setTimeout> | undefined;
    const sending = inFlight.current;
    const connection = networkConnection();
    const locked = hasLocks();

    const canSend = () => {
      if (!navigator.onLine) return false;
      if (wifiOnlyRef.current && wifiOnlySupported) return connection?.type === "wifi";
      return true;
    };

    const sendOne = async (item: QueuedUpload) => {
      // Claimed before any await, so an overlapping tick never picks the same photo.
      const controller = new AbortController();
      sending.set(item.id, controller);
      try {
        const claimed: QueuedUpload = { ...item, status: "sending", progress: 0 };
        await putItem(claimed);
        const result = await sendUpload(
          claimed,
          (fraction) => setProgress(item.id, fraction),
          controller.signal,
        );
        // Discarded mid-send: nothing to record.
        const current = await getItem(item.id);
        if (current?.status !== "sending") return;
        // Cut short by the app closing, not by the photo or the network: back in line as it was.
        if (controller.signal.reason === UNMOUNTED) {
          await putItem(backToWaiting(current));
          return;
        }
        await putItem(applyResult(current, result, Date.now()));
        if (result.kind === "sent") invalidateProject(item.projectId);
      } catch (error) {
        // A storage failure; the row is retried from wherever it was left.
        logger.warn("upload_queue_send_failed", { id: item.id, error: String(error) });
      } finally {
        sending.delete(item.id);
        clearProgress(item.id);
        tick();
      }
    };

    const tick = () => {
      if (!leader || stopped) return;
      clearTimeout(wakeTimer);
      if (!canSend()) return;
      // In flight when this read starts: a send that finishes during the read leaves the
      // map, but the read may still show its row as "sending". It is not orphaned.
      const inFlightAtStart = [...sending.keys()];
      void (async () => {
        let all: QueuedUpload[];
        try {
          all = await listItems();
        } catch (error) {
          logger.warn("upload_queue_read_failed", { error: String(error) });
          return;
        }
        if (!leader || stopped) return;
        // A row left "sending" by a failed save. With locks only this tab sends, so any such
        // row not in flight here is orphaned; without locks every tab sends, so skip it.
        if (locked) {
          const orphans = orphanedSending(all, new Set([...inFlightAtStart, ...sending.keys()]));
          if (orphans.length > 0) {
            try {
              await Promise.all(orphans.map(putItem));
            } catch (error) {
              logger.warn("upload_queue_requeue_failed", { error: String(error) });
            }
            // The writes notify, which ticks again with the fresh rows.
            return;
          }
        }
        const now = Date.now();
        const free = all.filter((i) => !sending.has(i.id));
        for (const item of pickNext(free, now, CONCURRENCY - sending.size)) void sendOne(item);
        // Wake for the next photo that is backing off.
        const nextDue = free
          .filter((i) => i.status === "waiting" && i.nextAttemptAt > now)
          .reduce((soonest, i) => Math.min(soonest, i.nextAttemptAt), Number.POSITIVE_INFINITY);
        if (Number.isFinite(nextDue)) {
          clearTimeout(wakeTimer);
          wakeTimer = setTimeout(tick, nextDue - now);
        }
      })();
    };
    tickRef.current = tick;

    const becomeLeader = async () => {
      if (stopped) return;
      leader = true;
      try {
        const { put, remove } = cleanupOnStart(await listItems(), Date.now());
        if (stopped) return;
        await Promise.all([...put.map(putItem), ...remove.map(deleteItem)]);
      } catch (error) {
        logger.warn("upload_queue_cleanup_failed", { error: String(error) });
      }
      tick();
    };

    const lockAbort = new AbortController();
    let releaseLock: (() => void) | undefined;
    if (locked) {
      navigator.locks
        .request(WORKER_LOCK, { signal: lockAbort.signal }, () => {
          // Granted after this provider already unmounted (StrictMode does that): hand it on.
          if (stopped) return Promise.resolve();
          void becomeLeader();
          // Held until this provider unmounts (or the tab closes).
          return new Promise<void>((resolve) => {
            releaseLock = resolve;
          });
        })
        .catch((error: unknown) => {
          // Aborted on unmount before the lock was granted: expected.
          if (error instanceof DOMException && error.name === "AbortError") return;
          // The lock itself failed (an opaque origin, a browser bug): send from this tab anyway.
          logger.warn("upload_queue_lock_failed", { error: String(error) });
          void becomeLeader();
        });
    } else {
      void becomeLeader();
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    window.addEventListener("online", tick);
    document.addEventListener("visibilitychange", onVisible);
    connection?.addEventListener("change", tick);

    return () => {
      stopped = true;
      leader = false;
      tickRef.current = () => {};
      clearTimeout(wakeTimer);
      for (const controller of sending.values()) controller.abort(UNMOUNTED);
      lockAbort.abort();
      releaseLock?.();
      window.removeEventListener("online", tick);
      document.removeEventListener("visibilitychange", onVisible);
      connection?.removeEventListener("change", tick);
    };
  }, [wifiOnlySupported, invalidateProject]);

  // Wi-Fi only switched in another tab.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== WIFI_ONLY_KEY) return;
      const value = event.newValue === "1";
      wifiOnlyRef.current = value;
      setWifiOnlyState(value);
      tickRef.current();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const timers = invalidateTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const enqueue = useCallback(async (input: UploadInput) => {
    const error = sizeError(input.blob.size);
    if (error) throw new Error(error);
    const id = input.id ?? crypto.randomUUID();
    await putItem(createUpload(input, id, Date.now()));
    askPersistentStorage();
    return id;
  }, []);

  const requeue = useCallback(async (ids: Set<string> | null) => {
    const now = Date.now();
    const all = await listItems();
    const again = all.filter(
      (i) =>
        (ids === null || ids.has(i.id)) &&
        (i.status === "failed" || (i.status === "waiting" && i.nextAttemptAt > now)),
    );
    await Promise.all(
      again.map((i) =>
        putItem({ ...i, status: "waiting", attempts: 0, nextAttemptAt: now, error: null }),
      ),
    );
  }, []);

  const retry = useCallback(
    (id: string) => {
      requeue(new Set([id])).catch((error: unknown) => {
        logger.warn("upload_queue_retry_failed", { id, error: String(error) });
      });
    },
    [requeue],
  );

  const retryAll = useCallback(() => {
    requeue(null).catch((error: unknown) => {
      logger.warn("upload_queue_retry_failed", { error: String(error) });
    });
  }, [requeue]);

  const keepDuplicate = useCallback((id: string) => {
    void (async () => {
      const item = await getItem(id);
      if (item?.status !== "duplicate") return;
      await putItem({
        ...item,
        status: "waiting",
        keepDuplicate: true,
        duplicateOf: null,
        nextAttemptAt: Date.now(),
        error: null,
      });
    })().catch((error: unknown) => {
      logger.warn("upload_queue_keep_duplicate_failed", { id, error: String(error) });
    });
  }, []);

  const discard = useCallback(async (id: string) => {
    inFlight.current.get(id)?.abort();
    await deleteItem(id);
  }, []);

  const clearSent = useCallback(async () => {
    const all = await listItems();
    await Promise.all(all.filter((i) => i.status === "sent").map((i) => deleteItem(i.id)));
  }, []);

  const setWifiOnly = useCallback((value: boolean) => {
    writeWifiOnly(value);
    wifiOnlyRef.current = value;
    setWifiOnlyState(value);
    tickRef.current();
  }, []);

  // A photo turns stale with time alone, so the summary re-reads the clock every minute
  // while anything is still to go up.
  const [clock, setClock] = useState(() => Date.now());
  const hasUnsent = items.some(
    (i) => i.status === "waiting" || i.status === "sending" || i.status === "failed",
  );
  useEffect(() => {
    if (!hasUnsent) return;
    const timer = setInterval(() => setClock(Date.now()), STALE_CHECK_MS);
    return () => clearInterval(timer);
  }, [hasUnsent]);
  const summary = useMemo(() => summarize(items, Math.max(clock, Date.now())), [items, clock]);

  const value = useMemo<UploadQueue>(
    () => ({
      items,
      summary,
      enqueue,
      retryAll,
      retry,
      keepDuplicate,
      discard,
      clearSent,
      wifiOnly,
      setWifiOnly,
      wifiOnlySupported,
    }),
    [
      items,
      summary,
      enqueue,
      retryAll,
      retry,
      keepDuplicate,
      discard,
      clearSent,
      wifiOnly,
      setWifiOnly,
      wifiOnlySupported,
    ],
  );

  return createElement(UploadQueueContext.Provider, { value }, children);
}

export function useUploadQueue(): UploadQueue {
  const queue = useContext(UploadQueueContext);
  if (!queue) throw new Error("useUploadQueue must be used inside UploadQueueProvider");
  return queue;
}
